import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { Button, Dialog, DialogContent, makeStyles, Paper, Typography } from '@material-ui/core'
import {
  Add,
  DeleteOutlineOutlined as DeleteIcon,
  EditOutlined as EditIcon
} from '@material-ui/icons'
import { useHistory } from 'react-router-dom'
import ConfigurationForm from 'views/admin/Badges/Configurations/ConfigurationForm'
import badgesSlice, { deleteBadgeConfiguration, getBadgeConfigurations } from 'store/badges'
import { useDispatch, useSelector } from 'react-redux'
import { renderCol } from 'utils/renderColumns'
import Tooltip from '@material-ui/core/Tooltip'
import BasicTable from 'components/BasicTable'
import ResourceNotFound from 'components/ResourceNotFound'
import getTableOptions from 'utils/getTableOptions'
import ConfirmModal from 'components/ConfirmationModal'

const ConfigurationListing = () => {
  const history = useHistory()
  const classes = useStyles()
  const dispatch = useDispatch()
  const { conference } = useSelector((state) => state.conference)
  const { badgeConfigurations, currentPage, pageSize } = useSelector((state) => state.badges)
  const [editedConfigurationId, setEditedConfigurationId] = useState('')
  const [deletedConfigurationId, setDeletedConfigurationId] = useState('')
  const [openConfigurationModal, setOpenConfigurationModal] = React.useState(false)
  const [deletingConfiguration, setDeletingConfiguration] = React.useState(false)

  useEffect(() => {
    if (conference?.id) {
      const unsub = getBadgeConfigurations(conference?.id, (data) => {
        dispatch(badgesSlice.actions.setBadgeConfigurations(data))
      })
      return () => unsub && unsub()
    }
  }, [conference?.id])

  const handleOpenConfigurationModal = () => {
    setOpenConfigurationModal(true)
  }

  const handleCloseConfigurationModal = () => {
    setOpenConfigurationModal(false)
    if (editedConfigurationId) setEditedConfigurationId('')
  }

  const handleCloseDeleteModal = () => {
    setDeletedConfigurationId('')
  }

  const handleDeleteConfiguration = async () => {
    setDeletingConfiguration(true)
    await deleteBadgeConfiguration(deletedConfigurationId)
    setDeletingConfiguration(false)
  }

  const goBack = () => history.goBack()

  const tableHead = [
    'Sr #',
    'Configuration Title',
    'Applied To',
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]
  const options = getTableOptions()

  const formatTableData = (badgeConfigurations) =>
    badgeConfigurations.map((configuration) => {
      return [
        configuration?.srNo,
        configuration?.title,
        configuration?.applicableSubjects?.join(', '),
        <>
          <Tooltip title="Edit" onClick={() => setEditedConfigurationId(configuration?.id)}>
            <EditIcon color="action" style={{ cursor: 'pointer' }} />
          </Tooltip>
          <Tooltip title="Delete" onClick={() => setDeletedConfigurationId(configuration?.id)}>
            <DeleteIcon color="action" style={{ marginLeft: 5, cursor: 'pointer' }} />
          </Tooltip>
        </>
      ]
    })

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h4">Badge Print Configurations</Typography>

        <Box sx={{ display: 'flex', gap: '0.75rem' }}>
          <Button
            variant="contained"
            color="primary"
            className={classes.addButton}
            onClick={goBack}>
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            className={classes.addButton}
            onClick={handleOpenConfigurationModal}>
            Add Badge Print Configuration
          </Button>
        </Box>
      </Box>

      {badgeConfigurations?.length ? (
        <Paper variant="outlined" style={{ marginTop: 20 }}>
          <BasicTable
            data={formatTableData(badgeConfigurations)}
            columns={tableHead}
            options={{
              ...options,
              onChangePage: (page) => dispatch(badgesSlice.actions.setCurrentPage(page)),
              onChangeRowsPerPage: (size) => dispatch(badgesSlice.actions.setPageSize(size)),
              page: currentPage,
              rowsPerPage: pageSize
            }}
          />
        </Paper>
      ) : (
        <ResourceNotFound message="No badge print configuration found" />
      )}

      <Dialog fullWidth maxWidth="xl" open={openConfigurationModal || !!editedConfigurationId}>
        <DialogContent className={classes.modalContent}>
          <ConfigurationForm
            configurationId={editedConfigurationId}
            title={
              editedConfigurationId
                ? 'Edit Badge Print Configuration'
                : 'Add Badge Print Configuration'
            }
            onClose={handleCloseConfigurationModal}
          />
        </DialogContent>
      </Dialog>

      <ConfirmModal
        dialogTitle="Delete Badge Print Configuration"
        dialogText="Do you really want to delete this badge print configuration?"
        open={!!deletedConfigurationId}
        closeModal={handleCloseDeleteModal}
        handleConfirm={handleDeleteConfiguration}
        inProgress={deletingConfiguration}
      />
    </Box>
  )
}

export default ConfigurationListing

const useStyles = makeStyles({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px'
  },
  addButton: {
    textTransform: 'none',
    fontWeight: 'bold'
  },
  modalContent: {
    paddingTop: '0px !important'
  }
})
